import { FC, FormEvent } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";

import Button from "@/components/Button";
import { FormLayout, Larger } from "@/components/Forms";
import { Modal } from "@/components/Modal";
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from "@/components/SidePanel";
import { api } from "@/utils/api";
import {
  FreelancerDetailsResource,
  InvoicesMemberResource,
} from "@/types/resources";
import { handleFormErrors } from "@/utils/helpers";
import useAlerts from "@/hooks/useAlerts";
import FormField from "@/components/Forms/FormField";
import {
  countries,
  debtorBanks,
  paymentTypes,
  recipientTypes,
} from "@/utils/data/payments";
import {
  BankBox,
  RemoveButton,
} from "@/pages/InvoiceList/components/InvoicesMemberModal";
import styled from "styled-components";
import Text from "@/components/Text";
import {
  BankBreak,
  BankNumber,
  BanksBlockHeading,
} from "@/pages/FreelancersList/components/FreelancerFormModal";
import InvoicesSwiftCodesField from "@/pages/InvoiceList/components/InvoicesSwiftCodesField";

export type FormDataT = {
  banks: {
    iban: string;
    swift_code: string;
    recipient_type: string;
    debtor_bank: string;
    bank: string;
    bank_country: string;
    payment_type: string;
    bank_address: string;
    note: string;
    true_receiver?: string;
    id?: number;
  }[];
};

const InnerFormLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-row-gap: 24px;
  grid-column-gap: 24px;
`;

const StyledBankBox = styled(BankBox)`
  &:first-child {
    margin-top: 0;
  }
`;

const AddButtonHolder = styled.div`
  button {
    width: 100%;
  }
`;

const StyledSidePanelContent = styled(SidePanelContent)`
  padding-top: 0;
`;

interface Props {
  freelancer: FreelancerDetailsResource;
  onClose: () => void;
}

const FreelancerBanksModal: FC<Props> = ({ onClose, freelancer }) => {
  const queryClient = useQueryClient();

  const alerts = useAlerts();

  const generateDefaultValues = (freelancer: FreelancerDetailsResource) => {
    return {
      banks: freelancer?.member.banks.length
        ? freelancer?.member.banks.map((bank) => {
            return {
              iban: bank.iban,
              swift_code: bank.swift_code,
              recipient_type: bank.recipient_type,
              debtor_bank: bank.debtor_bank,
              bank: bank.bank,
              bank_country: bank.bank_country,
              payment_type: bank.payment_type,
              bank_address: bank.bank_address,
              note: bank.note ?? "",
              true_receiver: bank.true_receiver ?? "",
              id: bank.id,
            };
          })
        : [
            {
              iban: "",
              swift_code: "",
              recipient_type: "",
              debtor_bank: "",
              bank: "",
              bank_country: "",
              payment_type: "",
              bank_address: "",
              note: "",
              true_receiver: "",
            },
          ],
    };
  };

  const {
    handleSubmit,
    setError,
    clearErrors,
    control,
    formState: { isSubmitting },
  } = useForm<FormDataT>({
    defaultValues: generateDefaultValues(freelancer),
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "banks",
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    handleSubmit(async (data) => {
      try {
        await api.put<FormDataT, InvoicesMemberResource>(
          `/v1/payments/freelancers/banks/${freelancer.id}`,
          {
            ...data,
          }
        );

        queryClient.invalidateQueries(["invoices_list"], {
          exact: false,
        });

        queryClient.invalidateQueries(["invoices_members"], {
          exact: true,
        });

        queryClient.invalidateQueries(["freelancers_details"], {
          exact: false,
        });

        alerts.success("Freelancer's banks has been succesfully updated");

        onClose();
      } catch (err) {
        handleFormErrors<FormDataT>(err, setError, alerts, true, false);
      }
    })(e);
  };

  return (
    <Modal stopPropagation={false}>
      <SidePanel heading="Edit bank details" onClose={onClose}>
        <SidePanelBody onSubmit={onSubmit} autoComplete="off">
          <StyledSidePanelContent>
            <FormLayout>
              {fields.map((_, index) => (
                <StyledBankBox key={index}>
                  <BanksBlockHeading>
                    <Text $type="caption">Bank details</Text>
                    <BankBreak />
                    <BankNumber>
                      <Text $type="smallBold" $color="white">
                        Nr. {index + 1}
                      </Text>
                    </BankNumber>
                  </BanksBlockHeading>
                  <InnerFormLayout>
                    <FormField<FormDataT>
                      name={`banks.${index}.true_receiver`}
                      control={control}
                      label="True Receiver"
                      placeholder="Add true receiver"
                    />
                    <FormField<FormDataT>
                      name={`banks.${index}.debtor_bank`}
                      control={control}
                      type="select"
                      label="Debtor bank"
                      options={debtorBanks}
                    />
                    <InvoicesSwiftCodesField
                      control={control}
                      name={`banks.${index}.swift_code`}
                      bankFieldName={`banks.${index}.bank`}
                      ibanFieldName={`banks.${index}.iban`}
                      countryFieldName={`banks.${index}.bank_country`}
                      paymentTypeFieldName={`banks.${index}.payment_type`}
                    />
                    <FormField<FormDataT>
                      name={`banks.${index}.recipient_type`}
                      control={control}
                      type="select"
                      label="Recipient type"
                      options={recipientTypes}
                    />
                    <FormField<FormDataT>
                      name={`banks.${index}.payment_type`}
                      control={control}
                      type="select"
                      label="Payment type"
                      options={paymentTypes}
                    />
                    <FormField<FormDataT>
                      name={`banks.${index}.bank_country`}
                      control={control}
                      type="select"
                      label="Bank country"
                      options={countries}
                    />
                    <FormField<FormDataT>
                      name={`banks.${index}.bank_address`}
                      control={control}
                      label="Bank address"
                      placeholder="Add address"
                    />
                    <FormField<FormDataT>
                      name={`banks.${index}.note`}
                      control={control}
                      label="Note"
                      type="textarea"
                      placeholder="Add note"
                    />
                    {index > 0 && (
                      <RemoveButton
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          remove(index);
                        }}
                      >
                        Remove section
                      </RemoveButton>
                    )}
                  </InnerFormLayout>
                </StyledBankBox>
              ))}
              <Larger>
                <AddButtonHolder>
                  <Button
                    type="button"
                    $type="colorized"
                    onClick={() =>
                      append({
                        iban: "",
                        swift_code: "",
                        recipient_type: "",
                        debtor_bank: "",
                        bank: "",
                        bank_country: "",
                        payment_type: "",
                        bank_address: "",
                        note: "",
                        true_receiver: "",
                      })
                    }
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M8 2V14"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M2 8H14"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    Add other bank
                  </Button>
                </AddButtonHolder>
              </Larger>
            </FormLayout>
          </StyledSidePanelContent>
          <SidePanelFooter $actions>
            <Button disabled={isSubmitting} loading={isSubmitting}>
              Update
            </Button>
            <Button
              color="secondary"
              onClick={(e: MouseEvent) => {
                e.preventDefault();
                onClose();
              }}
            >
              Cancel
            </Button>
          </SidePanelFooter>
        </SidePanelBody>
      </SidePanel>
    </Modal>
  );
};

export default FreelancerBanksModal;
